/**
 * Compatibility shim utilities for the @radix-ui/themes -> shadcn/Tailwind migration.
 *
 * The app imported layout/typography/control primitives from "@radix-ui/themes".
 * We alias that specifier (vite + tsconfig) to this local module so all 240+ call
 * sites keep working unchanged while we drop the dependency.
 *
 * Radix Themes ran at scaling="90%", so the pixel scales below are pre-multiplied
 * by 0.9 to faithfully reproduce the previous rendering.
 */
import type { CSSProperties } from "react";

const SCALING = 1;

const SPACE: Record<string, number> = {
  "0": 0,
  "1": 4,
  "2": 8,
  "3": 12,
  "4": 16,
  "5": 24,
  "6": 32,
  "7": 40,
  "8": 48,
  "9": 64,
};

const FONT_SIZE: Record<string, number> = {
  "1": 12,
  "2": 14,
  "3": 16,
  "4": 18,
  "5": 20,
  "6": 24,
  "7": 28,
  "8": 35,
  "9": 60,
};

const LINE_HEIGHT: Record<string, number> = {
  "1": 16,
  "2": 20,
  "3": 24,
  "4": 26,
  "5": 28,
  "6": 30,
  "7": 36,
  "8": 40,
  "9": 60,
};

const WEIGHT: Record<string, number> = {
  light: 300,
  regular: 400,
  medium: 500,
  bold: 700,
};

/** Resolve a Radix responsive prop to its base value. */
function base<T>(value: T | Record<string, T> | undefined): T | undefined {
  if (value && typeof value === "object") {
    const obj = value as Record<string, T>;
    return obj.initial ?? obj.xs ?? Object.values(obj)[0];
  }
  return value as T;
}

export function space(token: unknown): string | undefined {
  const t = base(token);
  if (t == null) return undefined;
  const key = String(t);
  if (key in SPACE) return `${SPACE[key] * SCALING}px`;
  return String(t); // arbitrary CSS length passthrough
}

export function fontSize(token: unknown): string | undefined {
  const t = base(token);
  if (t == null) return undefined;
  const key = String(t);
  if (key in FONT_SIZE) return `${FONT_SIZE[key] * SCALING}px`;
  return String(t);
}

export function lineHeight(token: unknown): string | undefined {
  const t = base(token);
  if (t == null) return undefined;
  const key = String(t);
  if (key in LINE_HEIGHT) return `${LINE_HEIGHT[key] * SCALING}px`;
  return undefined;
}

export function fontWeight(token: unknown): number | string | undefined {
  const t = base(token);
  if (t == null) return undefined;
  const key = String(t);
  return WEIGHT[key] ?? (t as string | number);
}

const ALIGN: Record<string, string> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  baseline: "baseline",
  stretch: "stretch",
};

const JUSTIFY: Record<string, string> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  between: "space-between",
  around: "space-around",
  evenly: "space-evenly",
};

export function alignItems(v: unknown): string | undefined {
  const t = base(v);
  return t ? (ALIGN[String(t)] ?? String(t)) : undefined;
}

export function justifyContent(v: unknown): string | undefined {
  const t = base(v);
  return t ? (JUSTIFY[String(t)] ?? String(t)) : undefined;
}

/** Radix accent color name -> CSS color (mapped onto our design tokens). */
const COLOR: Record<string, string> = {
  gray: "hsl(var(--muted-foreground))",
  slate: "hsl(var(--muted-foreground))",
  blue: "hsl(var(--info))",
  sky: "hsl(var(--info))",
  cyan: "hsl(var(--info))",
  indigo: "hsl(var(--info))",
  red: "hsl(var(--destructive))",
  tomato: "hsl(var(--destructive))",
  crimson: "hsl(var(--destructive))",
  ruby: "hsl(var(--destructive))",
  green: "hsl(var(--success))",
  grass: "hsl(var(--success))",
  jade: "hsl(var(--success))",
  teal: "hsl(var(--success))",
  mint: "hsl(var(--success))",
  amber: "hsl(var(--warning))",
  yellow: "hsl(var(--warning))",
  orange: "var(--brand-500)",
  brown: "var(--brand-600)",
  purple: "#8b5cf6",
  violet: "#7c3aed",
  iris: "var(--accent-iris)",
};

export function colorValue(name: unknown): string | undefined {
  const t = base(name);
  return t ? COLOR[String(t)] : undefined;
}

/** Keys consumed by the layout/margin style props on every Radix primitive. */
const MARGIN_KEYS = ["m", "mx", "my", "mt", "mr", "mb", "ml"] as const;
const SIZING_KEYS = [
  "width",
  "height",
  "minWidth",
  "maxWidth",
  "minHeight",
  "maxHeight",
] as const;
const POSITION_KEYS = [
  "position",
  "inset",
  "top",
  "right",
  "bottom",
  "left",
] as const;
const BOX_KEYS = [
  "display",
  "overflow",
  "overflowX",
  "overflowY",
  "flexBasis",
  "flexShrink",
  "flexGrow",
  "gridArea",
  "p",
  "px",
  "py",
  "pt",
  "pr",
  "pb",
  "pl",
] as const;

export function marginStyle(props: Record<string, unknown>): CSSProperties {
  const s: Record<string, string | undefined> = {};
  if (props.m != null) s.margin = space(props.m);
  if (props.mx != null) {
    s.marginLeft = space(props.mx);
    s.marginRight = space(props.mx);
  }
  if (props.my != null) {
    s.marginTop = space(props.my);
    s.marginBottom = space(props.my);
  }
  if (props.mt != null) s.marginTop = space(props.mt);
  if (props.mr != null) s.marginRight = space(props.mr);
  if (props.mb != null) s.marginBottom = space(props.mb);
  if (props.ml != null) s.marginLeft = space(props.ml);
  return s as CSSProperties;
}

export function paddingStyle(props: Record<string, unknown>): CSSProperties {
  const s: Record<string, string | undefined> = {};
  if (props.p != null) s.padding = space(props.p);
  if (props.px != null) {
    s.paddingLeft = space(props.px);
    s.paddingRight = space(props.px);
  }
  if (props.py != null) {
    s.paddingTop = space(props.py);
    s.paddingBottom = space(props.py);
  }
  if (props.pt != null) s.paddingTop = space(props.pt);
  if (props.pr != null) s.paddingRight = space(props.pr);
  if (props.pb != null) s.paddingBottom = space(props.pb);
  if (props.pl != null) s.paddingLeft = space(props.pl);
  return s as CSSProperties;
}

export function boxStyle(props: Record<string, unknown>): CSSProperties {
  const s: Record<string, unknown> = {
    ...marginStyle(props),
    ...paddingStyle(props),
  };
  for (const k of SIZING_KEYS) if (props[k] != null) s[k] = props[k];
  for (const k of POSITION_KEYS) if (props[k] != null) s[k] = props[k];
  if (props.display != null) s.display = props.display;
  if (props.overflow != null) s.overflow = props.overflow;
  if (props.overflowX != null) s.overflowX = props.overflowX;
  if (props.overflowY != null) s.overflowY = props.overflowY;
  if (props.flexBasis != null) s.flexBasis = props.flexBasis;
  if (props.flexShrink != null) s.flexShrink = props.flexShrink;
  if (props.flexGrow != null) s.flexGrow = props.flexGrow;
  if (props.gridArea != null) s.gridArea = props.gridArea;
  return s as CSSProperties;
}

/** All Radix style-prop keys, used to strip them before spreading to the DOM. */
export const STYLE_PROP_KEYS = new Set<string>([
  ...MARGIN_KEYS,
  ...SIZING_KEYS,
  ...POSITION_KEYS,
  ...BOX_KEYS,
  "direction",
  "align",
  "justify",
  "wrap",
  "gap",
  "gapX",
  "gapY",
  "columns",
  "rows",
  "flow",
  "areas",
  "size",
  "weight",
  "color",
  "variant",
  "highContrast",
  "radius",
  "trim",
  "truncate",
  "as",
  "asChild",
]);

/** Split incoming props into a style object + DOM-safe rest. */
export function splitProps(
  props: Record<string, unknown>,
  extraStyle: CSSProperties = {},
): { style: CSSProperties; rest: Record<string, unknown> } {
  const rest: Record<string, unknown> = {};
  for (const key of Object.keys(props)) {
    if (key === "style" || key === "className" || key === "children") continue;
    if (STYLE_PROP_KEYS.has(key)) continue;
    rest[key] = props[key];
  }
  const style = {
    ...boxStyle(props),
    ...extraStyle,
    ...((props.style as CSSProperties) ?? {}),
  };
  return { style, rest };
}
